/**
 * 53. Procurement Contracts — contract terms, renewal, linked POs.
 *
 * Arabic and English (C7): every word from the catalogs; statuses agree with
 * the contract.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { statusLabel } from "@/lib/i18n/records";
import { formatDate, formatQuantity } from "@/lib/money";

interface ProcurementContractRow {
  id: string;
  contractNumber: string;
  supplierName: string;
  title: string;
  startDate: string;
  endDate: string;
  linkedPoCount: number;
  status: "Active" | "Expiring" | "Expired" | "Draft" | "Cancelled";
}

export function ProcurementContractsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<ProcurementContractRow>(
    ["procurement", "contracts"],
    "/procurement/contracts",
  );

  return (
    <div>
      <PageHeader
        title={t("procurement.contracts.title")}
        subtitle={t("procurement.contracts.subtitle")}
        actions={
          <PendingButton variant="primary" reason="notScheduled" slice="B1">
            {t("procurement.contracts.new")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("procurement.contracts.list"), data.total)}>
          <DataTable<ProcurementContractRow>
            rows={data.items}
            columns={[
              {
                key: "contractNumber",
                label: t("fields.number"),
                render: (r) => (
                  <span className="atlas-mono">{r.contractNumber}</span>
                ),
              },
              { key: "title", label: t("fields.title") },
              { key: "supplierName", label: t("fields.supplier") },
              {
                key: "startDate",
                label: t("procurement.contracts.start"),
                render: (r) => formatDate(r.startDate),
              },
              {
                key: "endDate",
                label: t("procurement.contracts.end"),
                render: (r) => formatDate(r.endDate),
              },
              {
                key: "linkedPoCount",
                label: t("procurement.contracts.linkedPos"),
                numeric: true,
                render: (r) => formatQuantity(r.linkedPoCount, 0),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) =>
                  r.status === "Active" ? (
                    <StatusBadge status={r.status} type="ProcurementContract" />
                  ) : (
                    <Badge
                      tone={
                        r.status === "Expiring"
                          ? "warning"
                          : r.status === "Expired" || r.status === "Cancelled"
                            ? "danger"
                            : "default"
                      }
                    >
                      {statusLabel(t, "ProcurementContract", r.status)}
                    </Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
